import {useCallback, useMemo, useState} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {t} from 'i18next';
import {
  DataGridProProps,
  GridColDef,
  GridRowParams,
  GridEventListener,
} from '@mui/x-data-grid-pro';
import {ReactComponent as CloseIcon} from '../../assests/svg/close_icon.svg';
import AurButton from '../common/aur-button/AurButton';
import AurTreeDataGridPro from '../common/aur-tree-data-grid-pro/AurTreeDataGridPro';
import CustomTreeGroupCell, {
  GroupGridRenderCellParams,
} from '../common/aur-tree-data-grid-pro/CustomTreeGroupCell';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSet: (value: SearchRowType) => void;
  disablePortal?: boolean; // Use it for jest test case only
}

interface SearchRowType {
  hierarchy: string[];
  name: string;
  docs: number;
  searchId: string;
  lastModifiedBy: string;
  lastRunBy: string;
  lastRunDate: string;
  index: string;
  id: number;
}

const stringPath = 'eca.savedSearchSelectionDialog.';
const TABLE_BASE_WIDTH = 1229;
const mockedRowData: SearchRowType[] = [
  {
    hierarchy: ['List Item', 'SS-1234'],
    name: 'SS-1234',
    docs: 25,
    searchId: 'STR001',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '12/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 0,
  },
  {
    hierarchy: ['List Item', 'SS-4321'],
    name: 'SS-4321',
    docs: 25,
    searchId: 'STR002',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '13/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 1,
  },
  {
    hierarchy: ['List Item', 'SS-4431', 'SS-4550'],
    name: 'SS-4550',
    docs: 205,
    searchId: 'STR003',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '14/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 7,
  },
  {
    hierarchy: ['List Item', 'SS-4329'],
    name: 'SS-4329',
    docs: 25,
    searchId: 'STR003',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '15/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 2,
  },
  {
    hierarchy: ['List Item', 'SS-4431'],
    name: 'SS-4431',
    docs: 25,
    searchId: 'STR002',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '14/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 3,
  },
  {
    hierarchy: ['Document', 'SS-3431'],
    name: 'SS-3431',
    docs: 25,
    searchId: 'STR001',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '14/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 11,
  },
  {
    hierarchy: ['Document', 'SS-3432'],
    name: 'SS-3432',
    docs: 25,
    searchId: 'STR002',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '15/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 12,
  },
  {
    hierarchy: ['Document', 'SS-3433'],
    name: 'SS-3433',
    docs: 26,
    searchId: 'STR003',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '13/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 13,
  },
  {
    hierarchy: ['Document', 'SS-3434'],
    name: 'SS-3434',
    docs: 27,
    searchId: 'STR002',
    lastModifiedBy: 'Brandon Aubrey',
    lastRunBy: 'Dakota Prescott',
    lastRunDate: '13/12/24 01:24:55 UTC',
    index: 'Standard Index',
    id: 15,
  },
];

const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) =>
  row.hierarchy;

const ECASavedSearchSelectionDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSet,
  disablePortal,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedSearch, setSelectedSearch] = useState<SearchRowType | null>(
    null
  );
  const [haveReqError] = useState<boolean>(false);
  const [reqLoading] = useState<boolean>(false);
  const [searchData] = useState<SearchRowType[]>(mockedRowData);

  const columns: GridColDef<(typeof searchData)[number]>[] = useMemo(() => {
    return [
      {
        field: 'docs',
        headerName: t(stringPath + 'docs'),
        flex: 80 / TABLE_BASE_WIDTH,
        filterable: false,
        sortable: false,
        resizable: false,
      },
      {
        field: 'searchId',
        headerName: t(stringPath + 'searchId'),
        flex: 100 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
      },
      {
        field: 'lastModifiedBy',
        headerName: t(stringPath + 'lastModifiedBy'),
        flex: 181 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
      },
      {
        field: 'lastRunBy',
        headerName: t(stringPath + 'lastRunBy'),
        flex: 181 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
      },
      {
        field: 'lastRunDate',
        headerName: t(stringPath + 'lastRunDate'),
        flex: 184 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
      },
      {
        field: 'index',
        headerName: t(stringPath + 'index'),
        flex: 151 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
      },
    ];
  }, [t]);

  const handleRowSelection = useCallback((param: GridRowParams) => {
    setSelectedSearch(param.row);
  }, []);

  const handleCellClick: GridEventListener<'cellClick'> = (params, event) => {
    if (params.rowNode.type === 'group') {
      // Stop the event from propagating to the row click handler
      event.stopPropagation();
    }
  };

  const handleOnSetClick = () => {
    if (selectedSearch) onSet(selectedSearch);
  };

  const handleRefreshClick = () => {
    // Handle Refresh call
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      disablePortal={disablePortal}
      PaperProps={{className: 'eca-saved-search-selection-dialog-paper'}}
    >
      <DialogContent className="model-content">
        <>
          <div className="header-row">
            <Typography className="typo-text headline2_size title-label">
              {t(stringPath + 'title')}
            </Typography>
            <IconButton onClick={onClose} data-testid="close-icon-button">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="data-tree-table-container">
            <AurTreeDataGridPro
              rows={searchData}
              columns={columns}
              getTreeDataPath={getTreeDataPath}
              hideFooter
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              groupingColDef={{
                headerName: t(stringPath + 'savedSearches'),
                flex: 352 / TABLE_BASE_WIDTH,
                renderCell: (params) => {
                  return (
                    <CustomTreeGroupCell
                      params={params as GroupGridRenderCellParams}
                      isSelected={params.id === selectedSearch?.id}
                    />
                  );
                },
                filterable: true,
                resizable: false,
              }}
              onRowClick={handleRowSelection}
              onCellClick={handleCellClick}
              filterMode="client"
              noRowOverlayClass="no-row-container"
              isReqLoading={reqLoading}
              haveReqError={haveReqError}
              onErrorRefresh={handleRefreshClick}
              data-testid="saved-searched-tree-table"
            />
          </div>
        </>
      </DialogContent>
      <DialogActions className="footer-action-container">
        <AurButton
          type="exit"
          onClick={onClose}
          tabIndex={1}
          dataTestId="dialog-cancel-button"
          label={t('buttons.cancel')}
          size="Medium"
        />
        <AurButton
          classNames="button-set"
          type="continue"
          onClick={handleOnSetClick}
          tabIndex={2}
          dataTestId="dialog-set-button"
          label={t(stringPath + 'set')}
          size="Medium"
          disabled={selectedSearch === null}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ECASavedSearchSelectionDialog;
